/**
 * Lightweight client-side analytics. Logs events to console + localStorage
 * so a session can be inspected after-the-fact. If `VITE_ANALYTICS_URL` is
 * set, also POSTs each event to that endpoint (keepalive=true so the final
 * `ending_reached` survives page unload).
 *
 * Inspect from devtools: `__analytics.log()` returns the buffered events.
 */

const STORAGE_KEY = 'play-analytics-log';
const SESSION_KEY = 'play-analytics-session';
const MAX_EVENTS = 500;

export type AnalyticsEvent = {
  event: string;
  props?: Record<string, unknown>;
  ts: number;
  sessionId: string;
};

let sessionId: string | null = null;
function getSessionId(): string {
  if (sessionId) return sessionId;
  try {
    const existing = sessionStorage.getItem(SESSION_KEY);
    if (existing) { sessionId = existing; return existing; }
  } catch { /* private mode */ }
  const fresh = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 8)}`;
  try { sessionStorage.setItem(SESSION_KEY, fresh); } catch { /* */ }
  sessionId = fresh;
  return fresh;
}

const buffer: AnalyticsEvent[] = [];

function loadBuffer() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) buffer.push(...parsed);
    }
  } catch { /* corrupt; ignore */ }
}
loadBuffer();

function persist() {
  try {
    const keep = buffer.slice(-MAX_EVENTS);
    localStorage.setItem(STORAGE_KEY, JSON.stringify(keep));
  } catch { /* quota or private mode */ }
}

export function track(event: string, props?: Record<string, unknown>) {
  const entry: AnalyticsEvent = {
    event,
    props,
    ts: Date.now(),
    sessionId: getSessionId(),
  };
  buffer.push(entry);
  persist();
  console.info(`[analytics] ${event}`, props ?? '');
  const url = (import.meta as any).env?.VITE_ANALYTICS_URL as string | undefined;
  if (url) {
    try {
      void fetch(url, {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(entry),
        keepalive: true,
      });
    } catch { /* offline / blocked */ }
  }
}

export function getAnalyticsLog(): AnalyticsEvent[] {
  return buffer.slice();
}

export function clearAnalyticsLog() {
  buffer.length = 0;
  try { localStorage.removeItem(STORAGE_KEY); } catch { /* */ }
}

(window as unknown as { __analytics: unknown }).__analytics = {
  track,
  log: getAnalyticsLog,
  clear: clearAnalyticsLog,
};
